import type { Area, AreaShape, Condition, SceneState, Token, TokenKind, TokenSize } from '@/api/types';
import { newId } from '@/lib/id';

/** Casillas que ocupa cada tamaño (D&D 2024). Diminuto ocupa una pero se dibuja más pequeño. */
export const SIZE_CELLS: Record<TokenSize, number> = {
  tiny: 1,
  small: 1,
  medium: 1,
  large: 2,
  huge: 3,
  gargantuan: 4,
};
export const TOKEN_SIZES: readonly TokenSize[] = ['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan'];
export const TOKEN_KINDS: readonly TokenKind[] = ['pc', 'ally', 'neutral', 'enemy'];
export const AREA_SHAPES: readonly AreaShape[] = ['circle', 'square', 'cone', 'line'];
export const CONDITIONS: readonly Condition[] = [
  'concentrating',
  'prone',
  'grappled',
  'restrained',
  'frightened',
  'charmed',
  'poisoned',
  'blinded',
  'deafened',
  'invisible',
  'incapacitated',
  'stunned',
  'paralyzed',
  'unconscious',
  'petrified',
  'exhaustion',
];

/** Anillo de color según el bando: se lee de un vistazo quién es quién. */
export const KIND_RING: Record<TokenKind, string> = {
  pc: '#d9b45a',
  ally: '#6fae7b',
  neutral: '#9aa3ad',
  enemy: '#d0473b',
};

export const TOKEN_COLORS = [
  '#6d3a86',
  '#2f5d8a',
  '#8c2f2a',
  '#5b6770',
  '#3d7a48',
  '#b98a2c',
  '#2b2b2b',
  '#c05a28',
  '#3f8a87',
  '#9a4d6c',
] as const;

export const AREA_COLORS = [
  '#d2452f',
  '#e07a2b',
  '#e0b422',
  '#3f9a3a',
  '#2aa3a0',
  '#2f6fbf',
  '#6a3fa0',
  '#c23f86',
  '#8a8a8a',
  '#1f1f1f',
] as const;

export type PresetKey =
  | 'fireball'
  | 'spikeGrowth'
  | 'hungerOfHadar'
  | 'stinkingCloud'
  | 'fogCloud'
  | 'darkness'
  | 'silence'
  | 'spiritGuardians'
  | 'sleep'
  | 'hypnoticPattern'
  | 'faerieFire'
  | 'web'
  | 'burningHands'
  | 'coneOfCold'
  | 'dragonBreath'
  | 'lightningBolt'
  | 'gustOfWind';

export interface Preset {
  key: PresetKey;
  shape: AreaShape;
  size: number;
  width?: number;
  color: string;
}

/** Plantillas de conjuros de 2024, medidas en casillas (1 casilla = 1,5 m / 5 pies). */
export const PRESETS: readonly Preset[] = [
  { key: 'fireball', shape: 'circle', size: 4, color: '#d2452f' },
  { key: 'spikeGrowth', shape: 'circle', size: 4, color: '#3f9a3a' },
  { key: 'hungerOfHadar', shape: 'circle', size: 4, color: '#6a3fa0' },
  { key: 'stinkingCloud', shape: 'circle', size: 4, color: '#9aa52a' },
  { key: 'fogCloud', shape: 'circle', size: 4, color: '#8a9bb0' },
  { key: 'darkness', shape: 'circle', size: 3, color: '#1f1f1f' },
  { key: 'silence', shape: 'circle', size: 4, color: '#2aa3a0' },
  { key: 'spiritGuardians', shape: 'circle', size: 3, color: '#e0b422' },
  { key: 'sleep', shape: 'circle', size: 1, color: '#2f6fbf' },
  { key: 'hypnoticPattern', shape: 'square', size: 6, color: '#e0b422' },
  { key: 'faerieFire', shape: 'square', size: 4, color: '#c23f86' },
  { key: 'web', shape: 'square', size: 4, color: '#8a8a8a' },
  { key: 'burningHands', shape: 'cone', size: 3, color: '#e07a2b' },
  { key: 'coneOfCold', shape: 'cone', size: 12, color: '#6fb6d9' },
  { key: 'dragonBreath', shape: 'cone', size: 6, color: '#d2452f' },
  { key: 'lightningBolt', shape: 'line', size: 20, width: 1, color: '#e0b422' },
  { key: 'gustOfWind', shape: 'line', size: 12, width: 2, color: '#9fc6d6' },
];

/** Clave de traducción de la medida principal de cada forma. */
export function sizeLabelKey(shape: AreaShape) {
  return shape === 'circle' ? 'area.radius' : shape === 'square' ? 'area.side' : 'area.length';
}

export interface TokenDraft {
  name: string;
  kind: TokenKind;
  size: TokenSize;
  color: string;
}

export interface AreaDraft {
  name: string;
  shape: AreaShape;
  size: number;
  width: number;
  color: string;
  opacity: number;
}

export function makeToken(draft: TokenDraft, x: number, y: number): Token {
  return {
    id: newId(),
    name: draft.name,
    color: draft.color,
    size: draft.size,
    kind: draft.kind,
    x,
    y,
    hp: null,
    conditions: [],
    notes: '',
  };
}

export function makeArea(draft: AreaDraft, x: number, y: number): Area {
  return {
    id: newId(),
    name: draft.name,
    shape: draft.shape,
    size: draft.size,
    width: draft.width,
    angle: 0,
    color: draft.color,
    opacity: draft.opacity,
    x,
    y,
  };
}

/** "Bandido" → "Bandido 3" si ya hay Bandido, Bandido 2… */
export function nextName(base: string, taken: readonly string[]): string {
  const clean = base.trim().replace(/\s+\d+$/, '') || base.trim();
  const names = new Set(taken.map((n) => n.toLowerCase()));
  if (!names.has(clean.toLowerCase()) && !/\d$/.test(base.trim())) return clean;
  let n = 1;
  while (names.has(`${clean} ${n}`.toLowerCase())) n++;
  return `${clean} ${n}`;
}

/** Iniciales para la peana: "Bandido 2" → "B2", "Tobias Quemacardos" → "TQ", "Orion" → "OR". */
export function tokenTag(name: string): string {
  const s = name.trim();
  if (!s) return '?';
  const numbered = /^(.*?)\s*(\d+)$/.exec(s);
  if (numbered?.[1]) return (numbered[1].charAt(0) + (numbered[2] ?? '')).toUpperCase();
  const words = s.split(/\s+/);
  const tag = words.length > 1 ? (words[0]?.charAt(0) ?? '') + (words[1]?.charAt(0) ?? '') : s.slice(0, 2);
  return tag.toUpperCase();
}

/** Texto blanco o casi negro según la luminancia del fondo. */
export function readableOn(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#1b1b1b' : '#ffffff';
}

export function emptyScene(): SceneState {
  return {
    schemaVersion: 1,
    grid: { cols: 30, rows: 20, origin: 'corner', diagonals: 'standard' },
    display: { showAffectedCells: true, lockAreas: false, gridOpacity: 0.55 },
    map: null,
    tokens: [],
    areas: [],
  };
}

const pc = (name: string, color: string, x: number, y: number, max: number, conditions: Condition[] = []): Token => ({
  id: newId(),
  name,
  color,
  size: 'medium',
  kind: 'pc',
  x,
  y,
  hp: { current: max, max, temp: 0 },
  conditions,
  notes: '',
});

/** La escena de ejemplo: Crecimiento Espinoso y Hambre de Hadar superpuestos sobre un trol. */
export function exampleScene(names: { spikeGrowth: string; hungerOfHadar: string; burningHands: string }): SceneState {
  const enemy = (name: string, x: number, y: number, size: TokenSize, max: number, current = max): Token => ({
    id: newId(),
    name,
    color: size === 'large' ? '#3d7a48' : '#2b2b2b',
    size,
    kind: 'enemy',
    x,
    y,
    hp: { current, max, temp: 0 },
    conditions: [],
    notes: '',
  });
  return {
    schemaVersion: 1,
    grid: { cols: 26, rows: 18, origin: 'corner', diagonals: 'standard' },
    display: { showAffectedCells: true, lockAreas: false, gridOpacity: 0.55 },
    map: null,
    areas: [
      {
        id: newId(),
        name: names.spikeGrowth,
        shape: 'circle',
        size: 4,
        width: 1,
        angle: 0,
        color: '#3f9a3a',
        opacity: 0.32,
        x: 15,
        y: 9,
      },
      {
        id: newId(),
        name: names.hungerOfHadar,
        shape: 'circle',
        size: 4,
        width: 1,
        angle: 0,
        color: '#6a3fa0',
        opacity: 0.32,
        x: 17,
        y: 8,
      },
      {
        id: newId(),
        name: names.burningHands,
        shape: 'cone',
        size: 3,
        width: 1,
        angle: 330,
        color: '#e07a2b',
        opacity: 0.35,
        x: 9,
        y: 12,
      },
    ],
    tokens: [
      pc('Tobias', '#6d3a86', 4, 9, 43, ['concentrating']),
      pc('Orion', '#2f5d8a', 5, 12, 48),
      pc('Vlad', '#8c2f2a', 8, 6, 52),
      pc('Halvard', '#5b6770', 8, 11, 63),
      enemy('Trol', 15, 8, 'large', 94, 61),
      enemy('Bandido 1', 12, 10, 'medium', 11),
      { ...enemy('Bandido 2', 18, 5, 'medium', 11, 4), conditions: ['frightened'] },
      { ...enemy('Lobo', 20, 11, 'medium', 11), color: '#c05a28', conditions: ['prone'] },
    ],
  };
}
